import { html, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement, createUniqueId } from '../lib-element';
import '../button/lib-button';
import '../flyout/lib-flyout';
import '../action-bar/lib-action-bar';
import '../action-bar/lib-action-bar-item';
import '../icon/lib-icon';
import type { LibActionBar } from '../action-bar/lib-action-bar';
import css from './lib-sort-menu.css';

/** Sort-order picker with a floating action bar. */
@customElement('lib-sort-menu')
export class LibSortMenu extends LibElement {
  protected override get hostFocusSelector(): string { return '.lib-sort-menu-trigger'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  @property({ type: Number }) type = 0;
  @property() surface = 'white';
  @property() alignment = 'start';
  @property({ attribute: 'sort-label' }) sortLabel = 'Sort';
  @property({ attribute: 'alphabetical-label' }) alphabeticalLabel = 'Alphabetical';
  @property({ attribute: 'creation-date-label' }) creationDateLabel = 'Creation date';
  @state() private open = false;
  private readonly flyoutId = createUniqueId('lib-sort-menu');

  get value(): string { return String(this.type); }

  /** Closes the picker without changing its selection. */
  close() { this.open = false; }

  private selectionChanged(event: Event) {
    event.stopPropagation();
    const indexes: number[] = JSON.parse((event.currentTarget as LibActionBar).value);
    const next = indexes[0] + 1;
    if (next >= 1 && next <= 4 && next !== this.type) {
      this.type = next;
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    }
    this.close();
  }

  override render() {
    return html`<div class="lib-sort-menu">
      <lib-button variant="tertiary" class="lib-sort-menu-trigger"
        aria-label=${this.sortLabel} aria-expanded=${String(this.open)} aria-controls=${this.flyoutId}
        data-lib-flyout-anchor=${this.flyoutId} @click=${() => { this.open = !this.open; }}>
        <lib-icon icon-url=${new URL('../wago-icons-svg/sort.svg', import.meta.url).href}></lib-icon>
        <span class="lib-button-label">${this.sortLabel}</span>
      </lib-button>
      <lib-flyout id=${this.flyoutId} class="lib-sort-menu-flyout" .open=${this.open}
        alignment=${this.alignment} custom-surface @lib-flyout-dismiss=${(event: Event) => { event.stopPropagation(); this.close(); }}>
        <lib-action-bar position="left" surface=${this.surface} label-mode="visible" selection-mode="single"
          no-deselect floating active-index=${this.type >= 1 && this.type <= 4 ? String(this.type - 1) : ''}
          aria-label=${this.sortLabel} @change=${this.selectionChanged}>
          ${[1, 2, 3, 4].map(type => html`<lib-action-bar-item
            text=${type <= 2 ? this.alphabeticalLabel : this.creationDateLabel}
            icon-url=${new URL(`../wago-icons-svg/arrow-${type % 2 ? 'down' : 'up'}.svg`, import.meta.url).href}
            @click=${() => this.close()}></lib-action-bar-item>`)}
        </lib-action-bar>
      </lib-flyout>
    </div>`;
  }
}

/** Closes a sort picker after application navigation. */
export function closeSortMenu(element: LibSortMenu | null) { element?.close(); }

declare global { interface HTMLElementTagNameMap { 'lib-sort-menu': LibSortMenu; } }
